import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, Undo2 } from "lucide-react";
import { useAppDispatch } from "../../app/hooks";
import AiSteerChips, { useAiSteering } from "../AiSteerChips";
import DictationButton from "../DictationButton";
import {
  draftAnnouncement,
  improveAnnouncement,
} from "../../features/announcements/slice-extensions/announcements-slice-extension";

const MAX_SUBJECT = 200;
const MAX_BODY = 5000;

interface Props {
  subject: string;
  body: string;
  onSubjectChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * Subject + message fields shared by the announcement compose and template
 * dialogs: dictation lives inside the message field, and one adaptive AI action
 * below it either drafts from a description (empty message) or improves the
 * typed text in place — with a one-click undo.
 */
const AnnouncementMessageFields = ({
  subject,
  body,
  onSubjectChange,
  onBodyChange,
  disabled,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [aiStep, setAiStep] = useState<"idle" | "ask">("idle");
  const [aiMode, setAiMode] = useState<"improve" | "draft">("improve");
  const [aiInstruction, setAiInstruction] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [previous, setPrevious] = useState<{ subject: string; body: string } | null>(null);

  const hasBody = body.trim().length > 0;
  const canRunAi = !disabled && !aiBusy && (aiMode === "improve" || aiInstruction.trim().length >= 10);

  const chipOptions = [
    { key: "warmer", label: t("common.aiChipWarmer", "Warmer") },
    { key: "shorter", label: t("common.aiChipShorter", "Shorter") },
    { key: "polite", label: t("common.aiChipPolite", "More polite") },
    { key: "fixMistakes", label: t("common.aiChipFixMistakes", "Fix mistakes") },
  ];
  const steering = useAiSteering(chipOptions);

  const openAiStep = () => {
    setAiMode(hasBody ? "improve" : "draft");
    setAiInstruction("");
    steering.reset();
    setAiError(null);
    setAiStep("ask");
  };

  const closeAiStep = () => {
    setAiStep("idle");
    setAiError(null);
  };

  const handleAi = async () => {
    setAiBusy(true);
    setAiError(null);
    try {
      const result =
        aiMode === "improve"
          ? await dispatch(
              improveAnnouncement({
                subject: subject.trim() || null,
                body: body.trim(),
                instructions: steering.combine(aiInstruction) || null,
              }),
            ).unwrap()
          : await dispatch(draftAnnouncement({ prompt: aiInstruction.trim() })).unwrap();
      setPrevious({ subject, body });
      onSubjectChange(result.subject.slice(0, MAX_SUBJECT));
      onBodyChange(result.body.slice(0, MAX_BODY));
      setAiStep("idle");
    } catch {
      setAiError(
        t("announcements.aiError", "The assistant is unavailable right now. Please try again."),
      );
    } finally {
      setAiBusy(false);
    }
  };

  const handleUndo = () => {
    if (!previous) return;
    onSubjectChange(previous.subject);
    onBodyChange(previous.body);
    setPrevious(null);
  };

  return (
    <>
      <label className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
        {t("announcements.subjectLabel", "Subject")}
        <input
          type="text"
          className="input mt-1"
          value={subject}
          maxLength={MAX_SUBJECT}
          disabled={disabled}
          onChange={(event) => onSubjectChange(event.target.value)}
        />
      </label>

      <label className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
        {t("announcements.bodyLabel", "Message")}
        <span className="relative mt-1 block">
          <textarea
            className="input min-h-[140px] pr-10"
            value={body}
            maxLength={MAX_BODY}
            disabled={disabled}
            onChange={(event) => onBodyChange(event.target.value)}
          />
          <span className="absolute bottom-2 right-2">
            <DictationButton
              compact
              disabled={disabled || aiBusy}
              onText={(spoken) =>
                onBodyChange((body ? `${body} ${spoken}` : spoken).slice(0, MAX_BODY))
              }
            />
          </span>
        </span>
      </label>

      {aiStep === "idle" && (
        <div className="mt-2">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={disabled || aiBusy}
            onClick={openAiStep}
          >
            <Sparkles size={16} />
            {hasBody
              ? t("common.aiImprove", "Improve with AI")
              : t("common.aiDraft", "Draft with AI")}
          </button>
        </div>
      )}

      {aiStep === "ask" && (
        <div className="with-border mt-2 rounded-xl p-3">
          <p className="text-sm text-grey-30 dark:text-grey-70">
            {aiMode === "improve"
              ? t("announcements.aiAskImprove", "Anything you'd like changed? Optional — e.g. warmer, shorter.")
              : t("announcements.aiAskDescribe", "What should the message say? A few words are enough.")}
          </p>
          {aiMode === "improve" && (
            <div className="mt-2">
              <AiSteerChips
                options={chipOptions}
                selected={steering.selected}
                disabled={disabled || aiBusy}
                onToggle={steering.toggle}
              />
            </div>
          )}
          <input
            type="text"
            className="input mt-2"
            value={aiInstruction}
            maxLength={aiMode === "improve" ? 500 : 1000}
            disabled={disabled || aiBusy}
            placeholder={
              aiMode === "improve"
                ? t("announcements.aiSteerPlaceholder", "Optional: steer it — e.g. warmer, shorter")
                : t(
                    "announcements.aiDescribePlaceholder",
                    "e.g. No lesson next Friday — I'll offer make-up slots",
                  )
            }
            onChange={(event) => setAiInstruction(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && canRunAi) {
                event.preventDefault();
                handleAi();
              }
            }}
          />
          <div className="mt-2 flex items-center justify-between gap-2">
            <button
              type="button"
              className="text-sm text-primary-40 hover:underline dark:text-primary-90"
              disabled={aiBusy}
              onClick={closeAiStep}
            >
              {t("announcements.aiBack", "Back")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canRunAi}
              onClick={handleAi}
            >
              <Sparkles size={16} />
              {aiBusy
                ? aiMode === "improve"
                  ? t("common.aiImproving", "Improving…")
                  : t("common.aiDrafting", "Drafting…")
                : aiMode === "improve"
                  ? t("announcements.aiImproveNow", "Improve it")
                  : t("announcements.aiWriteNow", "Write it")}
            </button>
          </div>
        </div>
      )}

      {previous && (
        <p className="mt-2 flex items-center gap-2 text-xs text-grey-40 dark:text-grey-80">
          <Sparkles size={14} />
          {t("common.aiApplied", "AI suggestion applied.")}
          <button
            type="button"
            className="flex items-center gap-1 font-medium text-primary-40 hover:underline dark:text-primary-90"
            onClick={handleUndo}
          >
            <Undo2 size={14} />
            {t("common.aiUndo", "Undo")}
          </button>
        </p>
      )}

      {aiError && <p className="field-error mt-2">{aiError}</p>}
    </>
  );
};

export default AnnouncementMessageFields;
